import React from 'react';

export type UIComponentColorMode = 'light' | 'dark';

export interface BaseUIComponentProps {
    mode?: UIComponentColorMode;

    className?: string;
}

export interface GlobalDefaults {
    mode: UIComponentColorMode;
}

export const defaultBaseUIComponentProps: GlobalDefaults = {
    mode: 'dark',
};

export type HTMLTag = keyof HTMLElementTagNameMap;

/**
 * @summary React props for a given HTML tag name.
 *
 * @see AsProp - Using both AsProp and HTMLElemProps allows raw HTML props on a component, but it
 *               restricts them to the valid HTML attributes of the element specified by `as`.
 *               At compile time.
 */
type HTMLElemProps<Tag extends HTMLTag = HTMLTag> = Omit<
    React.DetailedHTMLProps<React.HTMLAttributes<Tag>, Tag>,
    'ref'
>;

/**
 * @summary The raw HTML props we usually want to expose through our component library API.
 */
export type ExposedHTMLProps = Partial<{
    className: string;
    title: string;
}>;

/**
 * @summary An `as` prop to specify the raw HTML element to use in a component library instance.
 *
 * If you need to add this to any component props, just use TypeScript good ol' & (e.g. MyComponentProps & AsProp<'span'>).
 *
 * @see HTMLElemProps - Using both AsProp and HTMLElemProps allows raw HTML props on a component,
 *                      but it restricts them to the valid HTML attributes of the element specified by `as`.
 *                      At compile time.
 */
export type AsProp<Tag extends HTMLTag = HTMLTag> = { as: Tag };

/**
 * @summary A `ref` prop to specify the React ref to an HTML element for the component instance.
 *
 * If you need to add this to any component props, just use TypeScript good ol' & (e.g. MyComponentProps & RefProp<'span'>).
 */
export type RefProp<Tag extends HTMLTag = HTMLTag> = { ref?: React.Ref<HTMLElementTagNameMap[Tag]> };

/**
 * @summary Base component to use the HTML element specified via the `as` prop.
 *
 * This component is where we have the combination of HTMLElemProps and AsProp.
 * For example:
 *
 * ```tsx
 *    <ReactElemProps as="span" value="invalid">Text</ReactElemProps>
 * ```
 *
 * Will have a red squiggle under `value="invalid"`, because "value" is not a valid HTML attribute for the <span> tag.
 */
export type ReactElemProps<Tag extends HTMLTag = HTMLTag> = HTMLElemProps<Tag> & AsProp<Tag> & RefProp<Tag>;
export function ReactHtmlNode<Tag extends HTMLTag = HTMLTag>({ as, ...htmlProps }: ReactElemProps<Tag>) {
    // If you can figure out how to implement this with JSX syntax, you're my hero.
    // At the end of the day it's transpiled to this method call, so it doesn't really matter.
    return React.createElement(as, htmlProps);
}
